import { Maximize, Minimize } from 'lucide-react';
import React, { FC, useState } from 'react';
import dayjs from 'dayjs';
import ReactMarkdown from 'react-markdown';

import {
  Button,
  Chip,
  TextButton,
  QuestionsCircleIcon,
  type IconProps,
  Spinner,
  Textarea,
  Heading,
  IconButton,
} from '../../shared/ui';
import { ChevronUpDownIcon } from '../../components/icons/chevron-up-down';
import { EditPencilIcon } from '../../components/icons/edit-pencil';
import { Love } from '../../components/icons/reactions/love';
import { Joy } from '../../components/icons/reactions/joy';
import { Anger } from '../../components/icons/reactions/anger';
import { Fear } from '../../components/icons/reactions/fear';
import { Sadness } from '../../components/icons/reactions/sadness';
import { WheelPanel } from '../../components/tools/life-balance-wheel/wheelPanel';
import { SandTherapyBoard } from '../../components/tools/sand-therapy/SandTherapyBoard';
import type { PlacedFigure } from '../../components/tools/sand-therapy/types';

import type { IJournalResult } from '../../store/types';
import { useGetCloudsImagePresignedQuery } from '../../services/clouds.api';

import type { Comments } from './types';
import { JournalToolsInfo } from './journal-tools-info';

type Props = {
  meetData?: IJournalResult | null;
  commentsData: Comments;
  setCommentsData: React.Dispatch<React.SetStateAction<Comments>>;
  onSave: () => void;
  onOpenConclusionTips: () => void;
  makeUrl: (url: string) => string;
  consultationId?: number;
  isGroupTherapy?: boolean;
};

const emotionSmile: Record<string, FC<IconProps>> = {
  Любовь: Love,
  Радость: Joy,
  Гнев: Anger,
  Страх: Fear,
  Грусть: Sadness,
};

const EditButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <TextButton
    endIcon={<EditPencilIcon width={24} height={24} stroke="currentColor" className="shrink-0" />}
    onClick={onClick}
    className="shrink-0 gap-2 text-label xs:text-label-lg"
  >
    Редактировать
  </TextButton>
);

/** Заголовок блока с кнопкой «Редактировать»: если в строку не помещаются, кнопка уходит под заголовок. */
const EditableBlockHeader: FC<{ title: string; isEditing: boolean; onStartEdit: () => void }> = ({
  title,
  isEditing,
  onStartEdit,
}) => (
  <div className="mb-[5px] flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
    <span className="font-medium text-ink">{title}</span>
    {!isEditing && <EditButton onClick={onStartEdit} />}
  </div>
);

/** «Сохранить» и «Отмена» под полем: рядом, пока каждой хватает 140px, иначе друг под другом. */
const EditActions: FC<{ onSave: () => void; onCancel: () => void }> = ({ onSave, onCancel }) => (
  <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
    <Button size="md" variant="dark" fullWidth onClick={onSave}>
      Сохранить
    </Button>
    <Button size="md" variant="outline" fullWidth onClick={onCancel}>
      Отмена
    </Button>
  </div>
);

/** Комментарий психолога с собственными кнопками — по одному блоку на каждое использование инструмента */
const PsychologistComment: FC<{
  value: string;
  isEditing: boolean;
  placeholder: string;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}> = ({ value, isEditing, placeholder, onStartEdit, onChange, onSave, onCancel }) => (
  <div className="min-w-0">
    <EditableBlockHeader title="Комментарии психолога" isEditing={isEditing} onStartEdit={onStartEdit} />
    {!isEditing && <div className="text-ink-muted">{value}</div>}
    {isEditing && (
      <>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[120px]"
          placeholder={placeholder}
        />
        <EditActions onSave={onSave} onCancel={onCancel} />
      </>
    )}
  </div>
);

// Компонент для отображения картинок облаков с функцией увеличения
const CloudsImageList: FC<{
  clouds: IJournalResult['clouds'];
  consultationId?: number;
  renderComment: (index: number) => React.ReactNode;
}> = ({ clouds, consultationId, renderComment }) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  // Компонент для полноэкранного режима
  const FullscreenImageModal: FC<{ image: string; consultationId?: number; onClose: () => void }> = ({
    image,
    consultationId,
    onClose,
  }) => {
    const { data: presignedData, isLoading } = useGetCloudsImagePresignedQuery(
      {
        consultationId: consultationId?.toString() || '',
        filename: image,
      },
      {
        skip: !consultationId || !image,
      },
    );

    const imageUrl = presignedData?.url;

    return (
      <div className="fixed inset-0 z-modal bg-black/80 flex items-center justify-center">
        <div className="flex flex-col w-full h-full">
          <div className="flex items-center justify-between p-2">
            <div className="flex-1 flex justify-start">
              <Chip tone="overlay" icon={<Minimize size={16} aria-hidden />} onClick={onClose}>
                Уменьшить
              </Chip>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            {isLoading ? (
              <Spinner className="text-white" />
            ) : imageUrl ? (
              <img src={imageUrl} alt="Облака" className="max-w-full max-h-full object-contain rounded-control" />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  // Компонент для отдельного изображения облака
  const CloudImageItem: FC<{ image: string; index: number; onFullscreen: () => void }> = ({
    image,
    index,
    onFullscreen,
  }) => {
    const { data: presignedData, isLoading } = useGetCloudsImagePresignedQuery(
      {
        consultationId: consultationId?.toString() || '',
        filename: image,
      },
      {
        skip: !consultationId || !image,
      },
    );

    const imageUrl = presignedData?.url;

    if (isLoading) {
      return (
        <div
          className="relative w-full max-w-[500px] rounded-control overflow-hidden bg-surface-muted flex items-center justify-center"
          style={{ minHeight: '300px' }}
        >
          <Spinner className="text-ink-subtle" />
        </div>
      );
    }

    if (!imageUrl) {
      return null;
    }

    return (
      <div className="relative w-full max-w-[500px] rounded-control overflow-hidden">
        <img src={imageUrl} alt="Облака" className="w-full h-auto" />
        <Chip
          tone="overlay"
          icon={<Maximize size={16} aria-hidden />}
          className="absolute top-2 left-2 z-20"
          onClick={onFullscreen}
        >
          Увеличить
        </Chip>
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-10">
        {clouds.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            {/* Изображение рисунка */}
            {el.image && <CloudImageItem image={el.image} index={idx} onFullscreen={() => setFullscreenIndex(idx)} />}
            {/* Время использования и номер картинки */}
            <div>
              <div className="flex gap-[15px] text-ui mb-[10px]">
                <div className="text-ink-muted">Время использования:</div>
                <div className="font-medium text-ink">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              <div className="flex gap-[15px] text-ui mb-[10px]">
                <div className="text-ink-muted">Номер картинки:</div>
                <div className="font-medium text-ink">{el.selected_image_index + 1}</div>
              </div>
              {renderComment(idx)}
            </div>
          </div>
        ))}
      </div>
      {/* Полноэкранный режим */}
      {fullscreenIndex !== null && clouds[fullscreenIndex]?.image && (
        <FullscreenImageModal
          image={clouds[fullscreenIndex].image}
          consultationId={consultationId}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
    </>
  );
};

export const JournalMainTab = ({
  meetData,
  commentsData,
  setCommentsData,
  onSave,
  onOpenConclusionTips,
  makeUrl,
  consultationId,
  isGroupTherapy = false,
}: Props) => {
  const ph = (forClient: string, forGroup: string) => (isGroupTherapy ? forGroup : forClient);

  const markdownComponents = {
    p: ({ className = '', ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className={`m-0 ${className}`.trim()} {...props} />
    ),
    ul: ({ className = '', ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className={`m-0 pl-5 list-disc ${className}`.trim()} {...props} />
    ),
    ol: ({ className = '', ...props }: React.HTMLAttributes<HTMLOListElement>) => (
      <ol className={`m-0 pl-5 list-decimal ${className}`.trim()} {...props} />
    ),
    li: ({ className = '', ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li className={`m-0 ${className}`.trim()} {...props} />
    ),
  };
  const hasDpdgContent = Boolean(
    meetData?.dpdg && ((meetData.dpdg.comment && meetData.dpdg.comment.trim().length > 0) || meetData.dpdg.date_time),
  );
  const hasQuestionsContent = Boolean(meetData?.questions && meetData.questions.length > 0);
  const hasWheelContent = Boolean(
    meetData?.wheel && meetData.wheel.some((item) => item.comment?.trim() || item.values?.length),
  );
  const hasEmotionsContent = Boolean(
    meetData?.emotions && meetData.emotions.some((emotion) => emotion.comment?.trim() || emotion.emotions?.length),
  );
  const hasMacContent = Boolean(
    meetData?.mac_cards &&
      meetData.mac_cards.some((card) => card.comment?.trim() || (Array.isArray(card.cards) && card.cards.length > 0)),
  );
  const hasColorCodeContent = Boolean(
    meetData?.color_code &&
      meetData.color_code.some((item) => item.colors?.length > 0 || item.comment?.trim() || item.analysis),
  );
  const hasCloudsContent = Boolean(
    meetData?.clouds && meetData.clouds.some((item) => item.image || item.comment?.trim()),
  );
  const hasSandTherapyContent = Boolean(
    meetData?.sand_therapy &&
      meetData.sand_therapy.some((item) => item.placed_figures?.length > 0 || item.comment?.trim()),
  );
  const hasGroupMacContent = Boolean(
    meetData?.mac_cards_by_participant &&
      Object.values(meetData.mac_cards_by_participant).some((arr) => arr?.some((e) => e.cards?.length)),
  );
  const hasGroupEmotionsContent = Boolean(
    meetData?.emotions_by_participant &&
      Object.values(meetData.emotions_by_participant).some((arr) => arr?.some((e) => e.emotions?.length)),
  );

  /** Участники групповой терапии: объединённый список identity с сохранением порядка (сначала по group_participant_names) */
  const groupParticipantIds = (() => {
    const fromMac = Object.keys(meetData?.mac_cards_by_participant ?? {});
    const fromEmo = Object.keys(meetData?.emotions_by_participant ?? {});
    const names = meetData?.group_participant_names ?? {};
    const set = new Set<string>([...fromMac, ...fromEmo]);
    const ordered = Object.keys(names).filter((id) => set.has(id));
    const rest = Array.from(set).filter((id) => !names[id]);
    return [...ordered, ...rest];
  })();
  const hasGroupTherapyContent = hasGroupMacContent || hasGroupEmotionsContent;

  const getParticipantLabel = (identity: string, index: number) =>
    meetData?.group_participant_names?.[identity] ?? `Участник ${index + 1}`;

  /** Редактирование одного блока «Комментарии психолога» в личной терапии (своя кнопка Редактировать/Сохранить у каждого инструмента) */
  type PersonalCommentTool = 'mac_cards' | 'emotions' | 'wheels' | 'color_code' | 'clouds' | 'sand_therapy' | 'dpdg';
  const [editingPersonalComment, setEditingPersonalComment] = useState<{
    tool: PersonalCommentTool;
    index: number;
  } | null>(null);

  const originalPersonalComment = (tool: PersonalCommentTool, index: number) => {
    switch (tool) {
      case 'mac_cards':
        return meetData?.mac_cards?.[index]?.comment ?? '';
      case 'emotions':
        return meetData?.emotions?.[index]?.comment ?? '';
      case 'wheels':
        return meetData?.wheel?.[index]?.comment ?? '';
      case 'color_code':
        return meetData?.color_code?.[index]?.comment ?? '';
      case 'clouds':
        return meetData?.clouds?.[index]?.comment ?? '';
      case 'sand_therapy':
        return meetData?.sand_therapy?.[index]?.comment ?? '';
      case 'dpdg':
        return meetData?.dpdg?.comment ?? '';
    }
  };

  const setPersonalComment = (tool: PersonalCommentTool, index: number, value: string) => {
    setCommentsData((prev) => {
      if (tool === 'dpdg') return { ...prev, dpdg: value };
      const next = [...(prev[tool] ?? [])];
      next[index] = value;
      return { ...prev, [tool]: next };
    });
  };

  const renderPersonalComment = (tool: PersonalCommentTool, index: number, placeholder: string) => (
    <PsychologistComment
      value={
        (tool === 'dpdg' ? commentsData.dpdg : commentsData[tool]?.[index]) ?? originalPersonalComment(tool, index)
      }
      isEditing={editingPersonalComment?.tool === tool && editingPersonalComment?.index === index}
      placeholder={placeholder}
      onStartEdit={() => setEditingPersonalComment({ tool, index })}
      onChange={(value) => setPersonalComment(tool, index, value)}
      onSave={() => {
        onSave();
        setEditingPersonalComment(null);
      }}
      onCancel={() => {
        setPersonalComment(tool, index, originalPersonalComment(tool, index));
        setEditingPersonalComment(null);
      }}
    />
  );

  /** Редактирование одного блока «Комментарии психолога» в групповой терапии (своя кнопка Редактировать/Сохранить у каждого) */
  type EditingGroupComment = { identity: string; tool: 'mac' | 'emotions'; index: number };
  const [editingGroupComment, setEditingGroupComment] = useState<EditingGroupComment | null>(null);

  const isEditingGroupBlock = (identity: string, tool: 'mac' | 'emotions', index: number) =>
    editingGroupComment?.identity === identity &&
    editingGroupComment?.tool === tool &&
    editingGroupComment?.index === index;

  const revertGroupComment = (identity: string, tool: 'mac' | 'emotions', index: number) => {
    if (tool === 'mac') {
      const orig = meetData?.mac_cards_by_participant?.[identity]?.[index]?.comment ?? '';
      setCommentsData((prev) => {
        const prevMap = { ...(prev.mac_cards_by_participant ?? {}) };
        const arr = [...(prevMap[identity] ?? [])];
        arr[index] = orig;
        prevMap[identity] = arr;
        return { ...prev, mac_cards_by_participant: prevMap };
      });
    } else {
      const orig = meetData?.emotions_by_participant?.[identity]?.[index]?.comment ?? '';
      setCommentsData((prev) => {
        const prevMap = { ...(prev.emotions_by_participant ?? {}) };
        const arr = [...(prevMap[identity] ?? [])];
        arr[index] = orig;
        prevMap[identity] = arr;
        return { ...prev, emotions_by_participant: prevMap };
      });
    }
    setEditingGroupComment(null);
  };

  const handleSaveGroupComment = () => {
    onSave();
    setEditingGroupComment(null);
  };

  /** Редактирование «Заключение по сессии» отдельно (для групповой терапии, когда общей кнопки нет) */
  const [editingConclusion, setEditingConclusion] = useState(false);
  const handleSaveConclusion = () => {
    onSave();
    setEditingConclusion(false);
  };
  const revertConclusion = () => {
    setCommentsData((prev) => ({ ...prev, result: meetData?.result ?? '' }));
    setEditingConclusion(false);
  };

  /** Редактирование «Вывод по сессии» по участнику в групповой терапии */
  const [editingSessionConclusionIdentity, setEditingSessionConclusionIdentity] = useState<string | null>(null);
  const handleSaveSessionConclusion = () => {
    onSave();
    setEditingSessionConclusionIdentity(null);
  };
  const revertSessionConclusion = (identity: string) => {
    const orig = meetData?.session_conclusion_by_participant?.[identity] ?? '';
    setCommentsData((prev) => ({
      ...prev,
      session_conclusion_by_participant: {
        ...(prev.session_conclusion_by_participant ?? {}),
        [identity]: orig,
      },
    }));
    setEditingSessionConclusionIdentity(null);
  };

  const infoMacCard =
    !meetData || !meetData.mac_cards || meetData.mac_cards.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.mac_cards.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            <div className="flex gap-[16px] flex-wrap">
              {el.cards.map((card, cardIdx) => (
                <div key={cardIdx} className="w-[120px] h-[160px] shrink-0 rounded-control overflow-hidden">
                  <img src={makeUrl(card)} className="w-full h-full object-cover" alt="" />
                </div>
              ))}
            </div>
            <div>
              <div className="flex gap-[15px] text-ui mb-[10px]">
                <div className="text-ink-muted">Время использования:</div>
                <div className="font-medium text-ink">{dayjs(el?.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              {renderPersonalComment(
                'mac_cards',
                idx,
                ph(
                  'Введите текст, например, ассоциации клиента, эврики, телесные “сигналы” и т.п.',
                  'Введите текст, например, ассоциации встречи, эврики, телесные “сигналы” и т.п.',
                ),
              )}
            </div>
          </div>
        ))}
      </div>
    );

  const infoEmotions =
    !meetData || !meetData.emotions || meetData.emotions.length === 0 ? null : (
      <>
        {meetData.emotions.map((el, idx) => (
          <div className="flex flex-col mb-[10px]" key={idx}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {el.emotions.map((emotion, emotionIdx) => {
                const SmileComponent = emotionSmile[emotion.category] || null;

                return (
                  <div className="flex flex-col md:flex-row gap-2" key={emotionIdx}>
                    <div className="w-[120px] h-[160px] shrink-0 rounded-control overflow-hidden flex">
                      <div className="w-full h-full box-border flex flex-col items-center justify-center gap-[15px] bg-surface-muted p-3 rounded-control text-center">
                        <div>
                          {SmileComponent ? (
                            <SmileComponent needHover={false} className="w-[80px] h-[80px] !cursor-default" />
                          ) : null}
                        </div>
                        <p className="font-medium text-ink">{emotion.category}</p>
                      </div>
                    </div>
                    <div>
                      <div className="w-full flex flex-wrap flex-col md:flex-row md:items-center gap-[5px] text-ink font-medium mb-[10px]">
                        {emotion.items.map((item, itemIdx) => (
                          <div className="bg-accent w-max p-[5px_15px] rounded-chip" key={itemIdx}>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-[15px] text-ui my-[10px]">
              <div className="text-ink-muted">Время использования:</div>
              <div className="font-medium text-ink">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
            </div>
            {renderPersonalComment(
              'emotions',
              idx,
              ph(
                'Введите текст, например, как клиент прокомментировал свое состояние',
                'Введите текст, например, как на встрече прокомментировали своё состояние',
              ),
            )}
          </div>
        ))}
      </>
    );

  /** Групповая терапия: разбивка по людям — раскрыть участника и видеть все его инструменты (МАК, эмоции) */
  const infoGroupByParticipant =
    !hasGroupTherapyContent || groupParticipantIds.length === 0 ? null : (
      <div className="flex flex-col gap-4">
        {groupParticipantIds.map((identity, participantIndex) => {
          const macEntries = meetData?.mac_cards_by_participant?.[identity];
          const emoEntries = meetData?.emotions_by_participant?.[identity];
          const hasMac = macEntries?.some((e) => e.cards?.length);
          const hasEmo = emoEntries?.some((e) => e.emotions?.length);
          if (!hasMac && !hasEmo) return null;

          return (
            <details key={identity} className="group overflow-hidden border-t border-b border-line">
              <summary className="list-none cursor-pointer px-4 py-4 pb-4 font-medium text-ink flex items-center justify-between gap-2">
                <span className="text-display-s">
                  {participantIndex + 1}. {getParticipantLabel(identity, participantIndex)}
                </span>
                <span className="shrink-0 transition-transform group-open:rotate-180" aria-hidden>
                  <ChevronUpDownIcon direction="down" width={24} height={24} stroke="#262633" />
                </span>
              </summary>
              <div className="p-4 flex flex-col gap-8 min-w-0">
                {hasMac && macEntries && (
                  <div className="flex flex-col gap-4">
                    <Heading size="sm">МАК-карты</Heading>
                    <div className="flex flex-col gap-10 min-w-0">
                      {macEntries.map((el, idx) => (
                        <div className="flex flex-col gap-10 min-w-0" key={idx}>
                          <div className="flex gap-[16px] flex-wrap">
                            {el.cards?.map((card, cardIdx) => (
                              <div
                                key={cardIdx}
                                className="w-[120px] h-[160px] shrink-0 rounded-control overflow-hidden"
                              >
                                <img src={makeUrl(card)} className="w-full h-full object-cover" alt="" />
                              </div>
                            ))}
                          </div>
                          <div className="min-w-0">
                            <div className="flex gap-[15px] text-ui mb-[10px]">
                              <div className="text-ink-muted">Время использования:</div>
                              <div className="font-medium text-ink">
                                {dayjs(el?.date_time).format('DD.MM.YYYY HH:mm')}
                              </div>
                            </div>
                            <EditableBlockHeader
                              title="Комментарии психолога"
                              isEditing={isEditingGroupBlock(identity, 'mac', idx)}
                              onStartEdit={() => setEditingGroupComment({ identity, tool: 'mac', index: idx })}
                            />
                            {!isEditingGroupBlock(identity, 'mac', idx) && (
                              <div className="text-ink-muted">
                                {commentsData.mac_cards_by_participant?.[identity]?.[idx] ?? el.comment ?? ''}
                              </div>
                            )}
                            {isEditingGroupBlock(identity, 'mac', idx) && (
                              <>
                                <Textarea
                                  value={commentsData.mac_cards_by_participant?.[identity]?.[idx] ?? ''}
                                  onChange={(e) => {
                                    setCommentsData((prev) => {
                                      const prevMap = { ...(prev.mac_cards_by_participant ?? {}) };
                                      const prevArr = [
                                        ...(prevMap[identity] ?? macEntries.map((en) => en.comment ?? '')),
                                      ];
                                      prevArr[idx] = e.target.value;
                                      prevMap[identity] = prevArr;
                                      return { ...prev, mac_cards_by_participant: prevMap };
                                    });
                                  }}
                                  className="min-h-[120px]"
                                  placeholder='Введите текст, например, ассоциации участника, эврики, телесные "сигналы" и т.п.'
                                />
                                <EditActions
                                  onSave={handleSaveGroupComment}
                                  onCancel={() => revertGroupComment(identity, 'mac', idx)}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {hasEmo && emoEntries && (
                  <div className="flex flex-col gap-4">
                    <Heading size="sm">Эмоциональное состояние</Heading>
                    <div className="flex flex-col gap-6 min-w-0">
                      {emoEntries.map((el, idx) => (
                        <div className="flex flex-col mb-[10px] min-w-0" key={idx}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {el.emotions?.map((emotion, emotionIdx) => {
                              const SmileComponent = emotionSmile[emotion.category] || null;
                              return (
                                <div className="flex flex-col md:flex-row gap-2" key={emotionIdx}>
                                  <div className="w-[120px] h-[160px] shrink-0 rounded-control overflow-hidden flex">
                                    <div className="w-full h-full box-border flex flex-col items-center justify-center gap-[15px] bg-surface-muted p-3 rounded-control text-center">
                                      <div>
                                        {SmileComponent ? (
                                          <SmileComponent
                                            needHover={false}
                                            className="w-[80px] h-[80px] !cursor-default"
                                          />
                                        ) : null}
                                      </div>
                                      <p className="font-medium text-ink">{emotion.category}</p>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="w-full flex flex-wrap flex-col md:flex-row md:items-center gap-[5px] text-ink font-medium mb-[10px]">
                                      {emotion.items?.map((item, itemIdx) => (
                                        <div className="bg-accent w-max p-[5px_15px] rounded-chip" key={itemIdx}>
                                          {item}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-[15px] text-ui my-[10px]">
                            <div className="text-ink-muted">Время использования:</div>
                            <div className="font-medium text-ink">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
                          </div>
                          <EditableBlockHeader
                            title="Комментарии психолога"
                            isEditing={isEditingGroupBlock(identity, 'emotions', idx)}
                            onStartEdit={() => setEditingGroupComment({ identity, tool: 'emotions', index: idx })}
                          />
                          {!isEditingGroupBlock(identity, 'emotions', idx) && (
                            <div className="text-ink-muted">
                              {commentsData.emotions_by_participant?.[identity]?.[idx] ?? el.comment ?? ''}
                            </div>
                          )}
                          {isEditingGroupBlock(identity, 'emotions', idx) && (
                            <>
                              <Textarea
                                value={commentsData.emotions_by_participant?.[identity]?.[idx] ?? ''}
                                onChange={(e) => {
                                  setCommentsData((prev) => {
                                    const prevMap = { ...(prev.emotions_by_participant ?? {}) };
                                    const prevArr = [
                                      ...(prevMap[identity] ?? emoEntries.map((en) => en.comment ?? '')),
                                    ];
                                    prevArr[idx] = e.target.value;
                                    prevMap[identity] = prevArr;
                                    return { ...prev, emotions_by_participant: prevMap };
                                  });
                                }}
                                className="min-h-[120px]"
                                placeholder="Введите текст, например, как участник прокомментировал своё состояние"
                              />
                              <EditActions
                                onSave={handleSaveGroupComment}
                                onCancel={() => revertGroupComment(identity, 'emotions', idx)}
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Вывод по сессии — внизу каждого участника */}
                <div className="flex flex-col gap-2 min-w-0 border-t border-line pt-6">
                  <EditableBlockHeader
                    title="Вывод по сессии"
                    isEditing={editingSessionConclusionIdentity === identity}
                    onStartEdit={() => setEditingSessionConclusionIdentity(identity)}
                  />
                  {editingSessionConclusionIdentity !== identity && (
                    <div className="text-ink-muted">
                      {commentsData.session_conclusion_by_participant?.[identity] ??
                        meetData?.session_conclusion_by_participant?.[identity] ??
                        ''}
                    </div>
                  )}
                  {editingSessionConclusionIdentity === identity && (
                    <>
                      <Textarea
                        value={commentsData.session_conclusion_by_participant?.[identity] ?? ''}
                        onChange={(e) => {
                          setCommentsData((prev) => ({
                            ...prev,
                            session_conclusion_by_participant: {
                              ...(prev.session_conclusion_by_participant ?? {}),
                              [identity]: e.target.value,
                            },
                          }));
                        }}
                        className="min-h-[120px]"
                        placeholder="Введите вывод по сессии для этого участника"
                      />
                      <EditActions
                        onSave={handleSaveSessionConclusion}
                        onCancel={() => revertSessionConclusion(identity)}
                      />
                    </>
                  )}
                </div>
              </div>
            </details>
          );
        })}
      </div>
    );

  const infoDpdg =
    !meetData || !meetData.dpdg || !hasDpdgContent ? null : (
      <>
        {meetData.dpdg?.date_time && (
          <div className="flex gap-[15px] text-ui mb-[10px]">
            <div className="text-ink-muted">Время использования:</div>
            <div className="font-medium text-ink">{dayjs(meetData.dpdg?.date_time).format('DD.MM.YYYY HH:mm')}</div>
          </div>
        )}
        {renderPersonalComment('dpdg', 0, 'Введите текст')}
      </>
    );

  const infoQuestions =
    !meetData || !meetData.questions || meetData.questions.length === 0 ? null : (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-[20px] gap-y-[10px]">
        {meetData.questions.map((el, idx) => (
          <div className="flex gap-[10px]" key={idx}>
            <span className="text-ink text-ui grow">{el.question}</span>
            <span className="text-ink-muted/60 text-ui">{dayjs(el?.date_time).format('HH:mm')}</span>
          </div>
        ))}
      </div>
    );

  const infoWheels =
    !meetData || !meetData.wheel || meetData.wheel.length === 0 ? null : (
      <>
        {meetData.wheel.map((el, idx) => (
          <div className="flex flex-col max-w-full mb-[10px]" key={idx}>
            <WheelPanel values={el.values} sectorNames={el.sectorNames} inJournal />

            <div className="flex gap-[15px] text-ui my-[10px]">
              <div className="text-ink-muted">Время использования:</div>
              <div className="font-medium text-ink">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
            </div>
            {renderPersonalComment(
              'wheels',
              idx,
              ph(
                'Введите текст, например, как клиент прокомментировал свое состояние',
                'Введите текст, например, как на встрече прокомментировали своё состояние',
              ),
            )}
          </div>
        ))}
      </>
    );

  const [expandedColorCode, setExpandedColorCode] = useState<Record<number, boolean>>({});

  const infoColorCode =
    !meetData || !meetData.color_code || meetData.color_code.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.color_code.map((item, idx) => {
          const isExpanded = expandedColorCode[idx] || false;

          return (
            <div className="flex flex-col gap-10" key={idx}>
              {/* Выбранные цвета */}
              <div className="flex gap-[16px] flex-wrap">
                {item.colors.map((color, colorIdx) => (
                  <div
                    key={colorIdx}
                    className="w-[61px] h-[78px] shrink-0 rounded-control"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Диагностическое заключение */}
              {item.analysis?.diagnostics && item.analysis.diagnostics.length > 0 && (
                <div>
                  <div className="font-medium text-ink mb-[5px]">Диагностическое заключение</div>
                  <div className="text-ink-muted mb-[10px] space-y-2">
                    {item.analysis.diagnostics.map((diagnostic, diagIdx) => (
                      <div key={diagIdx}>
                        <ReactMarkdown components={markdownComponents}>{diagnostic}</ReactMarkdown>
                      </div>
                    ))}
                  </div>
                  {(item.analysis.techniques?.length > 0 || item.analysis.hintQuestions?.length > 0) && (
                    <TextButton
                      size="md"
                      tone="primary"
                      className="self-start"
                      onClick={() => setExpandedColorCode((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                    >
                      {isExpanded ? 'Скрыть подробности' : 'Подробнее'}
                    </TextButton>
                  )}

                  {/* Раскрывающийся блок с techniques и hintQuestions */}
                  {isExpanded && item.analysis && (
                    <div className="flex flex-col gap-6 pl-4 border-l-2 border-line mt-4">
                      {item.analysis.hintQuestions && item.analysis.hintQuestions.length > 0 && (
                        <div>
                          <div className="font-medium text-ink mb-[5px]">Вопросы-подсказки</div>
                          <ul className="flex flex-col gap-2 text-ink-muted list-decimal list-inside">
                            {item.analysis.hintQuestions.map((question, qIdx) => (
                              <li key={qIdx} className="pl-2">
                                <ReactMarkdown components={markdownComponents}>{question}</ReactMarkdown>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {item.analysis.techniques && item.analysis.techniques.length > 0 && (
                        <div>
                          <div className="font-medium text-ink mb-[5px]">Техники</div>
                          <ul className="flex flex-col gap-2 text-ink-muted list-decimal list-inside">
                            {item.analysis.techniques.map((technique, techIdx) => (
                              <li key={techIdx} className="pl-2">
                                <ReactMarkdown components={markdownComponents}>{technique}</ReactMarkdown>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Время использования и комментарии психолога */}
              <div>
                <div className="flex gap-[15px] text-ui mb-[10px]">
                  <div className="text-ink-muted">Время использования:</div>
                  <div className="font-medium text-ink">{dayjs(item.date_time).format('DD.MM.YYYY HH:mm')}</div>
                </div>
                {renderPersonalComment('color_code', idx, 'Введите текст')}
              </div>
            </div>
          );
        })}
      </div>
    );

  const infoClouds =
    !meetData || !meetData.clouds || meetData.clouds.length === 0 ? null : (
      <CloudsImageList
        clouds={meetData.clouds}
        consultationId={consultationId}
        renderComment={(idx) =>
          renderPersonalComment(
            'clouds',
            idx,
            ph(
              'Введите текст, например, ассоциации клиента, эврики, телесные сигналы и т.п.',
              'Введите текст, например, ассоциации встречи, эврики, телесные сигналы и т.п.',
            ),
          )
        }
      />
    );

  const [sandTherapyFullscreenIndex, setSandTherapyFullscreenIndex] = useState<number | null>(null);

  const SAND_BOARD_W = 1200;
  const SAND_BOARD_H = 600;
  const SAND_PREVIEW_SCALE = 400 / SAND_BOARD_W;
  const SAND_PREVIEW_W = Math.round(SAND_BOARD_W * SAND_PREVIEW_SCALE);
  const SAND_PREVIEW_H = Math.round(SAND_BOARD_H * SAND_PREVIEW_SCALE);

  const infoSandTherapy =
    !meetData || !meetData.sand_therapy || meetData.sand_therapy.length === 0 ? null : (
      <div className="flex flex-col gap-10">
        {meetData.sand_therapy.map((el, idx) => (
          <div className="flex flex-col gap-10" key={idx}>
            <div
              className="relative rounded-control overflow-hidden"
              style={{ width: SAND_PREVIEW_W, height: SAND_PREVIEW_H }}
            >
              <div
                style={{
                  width: SAND_BOARD_W,
                  height: SAND_BOARD_H,
                  transform: `scale(${SAND_PREVIEW_SCALE})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                }}
              >
                <SandTherapyBoard
                  selectedFigures={[]}
                  placedFigures={el.placed_figures as PlacedFigure[]}
                  onPlaceFigure={() => {}}
                  onMoveFigure={() => {}}
                  onRotateFigure={() => {}}
                  onResizeFigure={() => {}}
                  onRemoveFigure={() => {}}
                  sandColor={el.sand_color}
                  readonly
                />
              </div>
              <Button
                size="sm"
                variant="overlay"
                startIcon={<Maximize size={16} aria-hidden />}
                className="absolute top-2 left-2 z-20"
                onClick={() => setSandTherapyFullscreenIndex(idx)}
              >
                Увеличить
              </Button>
            </div>
            <div>
              <div className="flex gap-[15px] text-ui mb-[10px]">
                <div className="text-ink-muted">Время использования:</div>
                <div className="font-medium text-ink">{dayjs(el.date_time).format('DD.MM.YYYY HH:mm')}</div>
              </div>
              <div className="flex gap-[15px] text-ui mb-[10px]">
                <div className="text-ink-muted">Фигур расставлено:</div>
                <div className="font-medium text-ink">{el.placed_figures.length}</div>
              </div>
              {renderPersonalComment(
                'sand_therapy',
                idx,
                'Введите текст, например, наблюдения по расстановке фигур, символика, гипотезы',
              )}
            </div>
          </div>
        ))}
        {sandTherapyFullscreenIndex !== null && meetData.sand_therapy[sandTherapyFullscreenIndex] && (
          <div className="fixed inset-0 z-modal bg-black/80 flex flex-col">
            <div className="flex-shrink-0 p-2">
              <Button
                size="sm"
                variant="overlay"
                startIcon={<Minimize size={16} aria-hidden />}
                onClick={() => setSandTherapyFullscreenIndex(null)}
              >
                Уменьшить
              </Button>
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto p-4">
              <div style={{ width: SAND_BOARD_W, height: SAND_BOARD_H, flexShrink: 0 }}>
                <SandTherapyBoard
                  selectedFigures={[]}
                  placedFigures={meetData.sand_therapy[sandTherapyFullscreenIndex].placed_figures as PlacedFigure[]}
                  onPlaceFigure={() => {}}
                  onMoveFigure={() => {}}
                  onRotateFigure={() => {}}
                  onResizeFigure={() => {}}
                  onRemoveFigure={() => {}}
                  sandColor={meetData.sand_therapy[sandTherapyFullscreenIndex].sand_color}
                  readonly
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );

  const footer = (
    <>
      <div className="w-full">
        <Heading size="md" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 mb-[10px]">
          <span className="flex items-center gap-[5px]">
            <span>Заключение по сессии</span>
            <IconButton label="Возможная структура заключения" className="-m-1.5" onClick={onOpenConclusionTips}>
              <QuestionsCircleIcon />
            </IconButton>
          </span>
          {!editingConclusion && <EditButton onClick={() => setEditingConclusion(true)} />}
        </Heading>

        {!editingConclusion && <div className="text-ink-muted break-all">{commentsData.result}</div>}
        {editingConclusion && (
          <>
            <Textarea
              value={commentsData.result}
              onChange={(e) => setCommentsData((prev) => ({ ...prev, result: e.target.value }))}
              className="min-h-[120px]"
              placeholder={ph(
                'Введите текст, например, тип клиента, запрос, рабочие гипотезы, техники, методы, интервенция, что клиент “забрал” и т.п.',
                'Введите текст, например, тип встречи, запрос, рабочие гипотезы, техники, методы, интервенция, что «забрали» с встречи и т.п.',
              )}
            />
            <EditActions onSave={handleSaveConclusion} onCancel={revertConclusion} />
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      {hasMacContent && (
        <JournalToolsInfo title={'Мак-карта'} assignmentId="mac_cards">
          {infoMacCard}
        </JournalToolsInfo>
      )}

      {hasGroupTherapyContent && (
        <JournalToolsInfo title={'Работа с инструментами по участникам'}>{infoGroupByParticipant}</JournalToolsInfo>
      )}

      {hasWheelContent && (
        <JournalToolsInfo title={'Колесо баланса'} assignmentId="wheel">
          {infoWheels}
        </JournalToolsInfo>
      )}

      {hasEmotionsContent && (
        <JournalToolsInfo title={'Эмоциональное состояние'} assignmentId="emotions">
          {infoEmotions}
        </JournalToolsInfo>
      )}

      {hasDpdgContent && (
        <JournalToolsInfo title={'Десенсибилизация и переработка движением глаз (ДПДГ)'} assignmentId="dpdg">
          {infoDpdg}
        </JournalToolsInfo>
      )}
      {hasQuestionsContent && <JournalToolsInfo title={'Используемые вопросы'}>{infoQuestions}</JournalToolsInfo>}

      {hasColorCodeContent && (
        <JournalToolsInfo title={'Цветовой код'} assignmentId="color_code">
          {infoColorCode}
        </JournalToolsInfo>
      )}

      {hasCloudsContent && (
        <JournalToolsInfo title={'Облака'} assignmentId="clouds">
          {infoClouds}
        </JournalToolsInfo>
      )}

      {hasSandTherapyContent && (
        <JournalToolsInfo title={'Песочная терапия'} assignmentId="sand_therapy">
          {infoSandTherapy}
        </JournalToolsInfo>
      )}

      {footer}
    </>
  );
};
